import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { get, patch } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';

function Menu({ website, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState((website && (website?.menu?.items || [])) || []);
  const [pages, setPages] = useState([]);
  const [dragPath, setDragPath] = useState(null);
  const [overPath, setOverPath] = useState(null);
  const [overPos, setOverPos] = useState(null);
  const itemRefs = useRef({});

  useEffect(() => {
    if (!website?.id) return;
    get(`websites/${website.id}/pages`)
      .then(resp => setPages(Array.isArray(resp) ? resp : (resp?.pages || [])))
      .catch(() => toast.error('Failed to load pages.'));
  }, [website?.id]);

  if (!website) {
    return <Alert variant="warning">Website not found.</Alert>;
  }

  const addItem = () => {
    setItems(prev => [...prev, { pageId: '', name: '', children: [] }]);
  };

  const getByPath = (list, path) => {
    let node = { children: list };
    for (let i = 0; i < path.length; i++) {
      const idx = path[i];
      node = (node.children || [])[idx];
      if (!node) return undefined;
    }
    return node;
  };

  const setByPath = (list, path, updater) => {
    const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
    const next = clone(list);
    let node = { children: next };
    for (let i = 0; i < path.length; i++) {
      node = (node.children || [])[path[i]];
    }
    const updated = updater(node);
    let p = { children: next };
    for (let i = 0; i < path.length - 1; i++) {
      p = p.children[path[i]];
    }
    if (path.length === 0) return updated.children || [];
    p.children[path[path.length - 1]] = updated;
    return next;
  };

  const removeByPath = (list, path) => {
    const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
    const next = clone(list);
    let parent = { children: next };
    for (let i = 0; i < path.length - 1; i++) {
      parent = parent.children[path[i]];
    }
    const idx = path[path.length - 1];
    const [removed] = (parent.children || []).splice(idx, 1);
    return [removed, parent.children ? next : (removed ? [] : next)];
  };

  const insertAtPath = (list, path, pos, node) => {
    const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
    const next = clone(list);
    if (pos === 'inside') {
      const target = getByPath(next, path);
      if (target) {
        if (!Array.isArray(target.children)) target.children = [];
        target.children.push(node);
      }
    } else {
      let parent = { children: next };
      for (let i = 0; i < path.length - 1; i++) parent = parent.children[path[i]];
      const idx = path[path.length - 1] + (pos === 'after' ? 1 : 0);
      (parent.children || []).splice(idx, 0, node);
    }
    return next;
  };

  const pathKey = (path) => (path || []).join('.');

  const updateFieldAtPath = (path, field, value) => {
    setItems(prev => {
      const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
      const next = clone(prev);
      const node = getByPath(next, path);
      if (!node) return prev;
      node[field] = value;
      return [...next];
    });
  };

  const onDragStart = (path, e) => {
    setDragPath(path);
    setOverPath(path);
    try { e.dataTransfer.setData('text/plain', path.join('.')); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    const el = itemRefs.current[pathKey(path)];
    if (el && typeof e.dataTransfer.setDragImage === 'function') {
      e.dataTransfer.setDragImage(el, 16, 16);
    }
  };

  const onDragEnter = (path) => {
    if (!dragPath) return;
    const sameParent = dragPath.length === path.length && dragPath.slice(0, -1).every((v, i) => v === path[i]);
    if (!sameParent) return;
    if (!overPath || pathKey(path) !== pathKey(overPath)) setOverPath(path);
  };

  const onDragOver = (path, depth, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!dragPath) return;
    const sameParent = dragPath.length === path.length && dragPath.slice(0, -1).every((v, i) => v === path[i]);
    if (!sameParent) {
      setOverPath(null);
      setOverPos(null);
      return;
    }
    const el = itemRefs.current[pathKey(path)];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const y = e.clientY - rect.top;
    setOverPath(path);
    setOverPos(y / rect.height < 0.5 ? 'before' : 'after');
  };

  const onDrop = (path, depth, e) => {
    e.preventDefault();
    if (!dragPath || !Array.isArray(dragPath)) return;
    const sameParent = dragPath.length === path.length && dragPath.slice(0, -1).every((v, i) => v === path[i]);
    if (!sameParent || pathKey(path) === pathKey(dragPath)) {
      setDragPath(null); setOverPath(null); setOverPos(null);
      return;
    }
    setItems(prev => {
      const [removed, afterRemove] = removeByPath(prev, dragPath);
      if (!removed) return prev;
      let targetPath = path.slice();
      const fromIdx = dragPath[dragPath.length - 1];
      const toIdx = path[path.length - 1];
      if (fromIdx < toIdx) targetPath[targetPath.length - 1] = toIdx - 1;
      return insertAtPath(afterRemove, targetPath, overPos || 'after', removed);
    });
    setDragPath(null);
    setOverPath(null);
    setOverPos(null);
  };

  const onDragEnd = () => {
    setDragPath(null);
    setOverPath(null);
    setOverPos(null);
  };

  const save = async (e) => {
    e.preventDefault();
    let invalid = '';
    const visit = (node, depth, rowLabel) => {
      if (depth > 2) { invalid = `${rowLabel}: nesting exceeds 2 levels`; return; }
      if (!node.name || !node.name.trim()) { invalid = `${rowLabel}: label is required`; return; }
      if (!node.pageId) { invalid = `${rowLabel}: page is required`; return; }
      const children = node.children || [];
      for (let i = 0; i < children.length && !invalid; i++) {
        visit(children[i], depth + 1, `${rowLabel}.${i + 1}`);
      }
    };
    for (let i = 0; i < items.length && !invalid; i++) visit(items[i], 1, `Row ${i + 1}`);
    if (invalid) { toast.error(invalid); return; }
    try {
      setSaving(true);
      const payload = { menu: items };
      const resp = await patch(`websites/${website.id}/menu`, payload);
      const updated = resp?.website || { ...website, menu: items };
      onUpdated?.(updated);
      toast.success('Menu saved.');
    } catch (e) {
      toast.error('Failed to save menu.');
    } finally {
      setSaving(false);
    }
  };

  const renderItem = (it, path, depth) => {
    const pk = pathKey(path);
    const isDragging = dragPath && pathKey(dragPath) === pk;
    const isOver = overPath && pathKey(overPath) === pk;
    const showTopLine = isOver && overPos === 'before' && !isDragging;
    const showBottomLine = isOver && overPos === 'after' && !isDragging;
    const children = it.children || [];

    return (
      <div
        key={pk}
        className={`menu-item${isDragging ? ' menu-item--dragging' : ''}${isOver ? ' menu-item--over' : ''}`}
        onDragEnter={() => onDragEnter(path)}
        onDragOver={(e) => onDragOver(path, depth, e)}
        onDrop={(e) => onDrop(path, depth, e)}
        ref={(el) => (itemRefs.current[pk] = el)}
      >
        {showTopLine && <div className="drop-indicator drop-indicator--top" />}
        {showBottomLine && <div className="drop-indicator drop-indicator--bottom" />}

        <button
          type="button"
          aria-label="Drag to reorder"
          title="Drag to reorder"
          draggable
          onDragStart={(e) => onDragStart(path, e)}
          onDragEnd={onDragEnd}
          className="menu-drag-handle"
        >⠿</button>

        <div className="menu-item-row">
          <Form.Control
            className="menu-item-name"
            type="text"
            value={it.name || ''}
            onChange={(e) => updateFieldAtPath(path, 'name', e.target.value)}
            placeholder="Link label"
          />
          <Form.Select
            className="menu-item-page"
            value={it.pageId || ''}
            onChange={(e) => updateFieldAtPath(path, 'pageId', e.target.value)}
          >
            <option value="">— select page —</option>
            {pages.map(p => (
              <option key={p.id} value={p.id}>{p.title || p.slug || p.id}</option>
            ))}
          </Form.Select>
          {depth < 2 && (
            <button
              type="button"
              className="menu-add-sub-btn"
              title="Add a child link under this item"
              onClick={() => setItems(prev => setByPath(prev, path, (node) => ({
                ...node,
                children: [...(node.children || []), { name: '', pageId: '', children: [] }]
              })))}
            >↳</button>
          )}
          <button
            type="button"
            aria-label="Remove"
            title="Remove"
            onClick={() => setItems(prev => { const [, next] = removeByPath(prev, path); return next; })}
            className="menu-remove-btn"
          >×</button>
        </div>

        {children.length > 0 && (
          <div className="menu-children">
            {children.map((ch, idx) => renderItem(ch, [...path, idx], depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-4">
      <h5 className="mb-3">Menu Editor</h5>
      <div className="menu-panel">
        <div className="menu-panel-header">
          <span className="menu-panel-col-label">Link label</span>
          <span className="menu-panel-col-path">Page</span>
          <span className="menu-panel-col-hint">⠿ drag to reorder &nbsp;·&nbsp; ↳ add child link</span>
        </div>
        <Form onSubmit={save}>
          <div className="menu-list">
            {items.length === 0 ? (
              <div className="menu-empty">
                <div className="menu-empty-icon">☰</div>
                <div className="menu-empty-title">No navigation items yet</div>
                <p className="mb-0">Click "Add item" below to build your site's navigation.</p>
              </div>
            ) : (
              items.map((it, idx) => renderItem(it, [idx], 1))
            )}
          </div>
          <div className="menu-panel-footer">
            <Button type="button" className="primary-button" size="sm" onClick={addItem}>
              + Add item
            </Button>
            <Button type="submit" className="primary-button" disabled={saving}>
              {saving ? (<><Spinner animation="border" size="sm" className="me-2" />Saving...</>) : 'Save menu'}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default Menu;
